import Pusher, { Channel } from 'pusher-js';
import mitt from 'mitt';
import { axiosInstance } from '@/boot/axios';
import { OrganizationMembership, Team, User } from '@/models/user.models';
import {
  PusherAIChatMessageEvent,
  PusherCommentArchivedEvent,
  PusherCommentCreatedEvent,
  PusherCommentUpdatedEvent,
  PusherEvent,
  PusherFolderArchivedEvent,
  PusherFolderCreatedEvent,
  PusherFolderUpdatedEvent,
  PusherInitiativeCreatedEvent,
  PusherInitiativeDeletedEvent,
  PusherInitiativeUpdatedEvent,
  PusherJobUpdatedEvent,
  PusherMilestoneCreatedEvent,
  PusherMilestoneDeletedEvent,
  PusherMilestoneUpdatedEvent,
  PusherNotificationCreatedEvent,
  PusherActivityLogCreatedEvent,
  PusherScenarioCreatedEvent,
  PusherScenarioDeletedEvent,
  PusherScenarioUpdatedEvent,
  PusherWorkItemArchivedEvent,
  PusherWorkItemCreatedEvent,
  PusherWorkItemUpdatedEvent,
  PusherBoardFolderCreatedEvent,
  PusherBoardFolderUpdatedEvent,
  PusherBoardCreatedEvent,
  PusherBoardUpdatedEvent,
  PusherBoardArchivedEvent,
  PusherFolderDeletedEvent,
  PusherWorkItemDeletedEvent,
  PusherFolderUnarchivedEvent,
  PusherWorkItemUnarchivedEvent,
  ChunkedPusherEvent,
  PusherPermissionUpdatedEvent,
  PusherBoardPermissionUpdatedEvent,
  PusherTimeEntryCreatedEvent,
  PusherTimeEntryUpdatedEvent,
  PusherTimeEntryDeletedEvent,
  PusherDraftDescriptionUpdatedEvent,
  PusherDatabaseCredentialUpdatedEvent,
  PusherDatabaseCredentialDeletedEvent,
} from '@/models/pusher.models';
import { PreparedItem } from '@/models/item.models';
import { useJobsStore } from '@/store/jobsStore';
import { Product } from '@/api';
import { Board, BoardFolder } from '@/api';
import { useUserStore } from './userStore';
import { Organization, Notification, Workflow } from '@/api';

export type ChannelItemEventsMap = {
  'folder-created': PusherFolderCreatedEvent;
  'folder-updated': PusherFolderUpdatedEvent;
  'folder-archived': PusherFolderArchivedEvent;
  'folder-unarchived': PusherFolderUnarchivedEvent;
  'folder-deleted': PusherFolderDeletedEvent;
  'work-item-created': PusherWorkItemCreatedEvent;
  'work-item-updated': PusherWorkItemUpdatedEvent;
  'work-item-archived': PusherWorkItemArchivedEvent;
  'work-item-unarchived': PusherWorkItemUnarchivedEvent;
  'work-item-deleted': PusherWorkItemDeletedEvent;
  'activity-log-created': PusherActivityLogCreatedEvent;
  'draft-description-updated': PusherDraftDescriptionUpdatedEvent;
};

export type ChannelCommentEventsMap = {
  'comment-created': PusherCommentCreatedEvent;
  'comment-updated': PusherCommentUpdatedEvent;
  'comment-archived': PusherCommentArchivedEvent;
};

export type ChannelTimeTrackingEventsMap = {
  'time-entry-created': PusherTimeEntryCreatedEvent;
  'time-entry-updated': PusherTimeEntryUpdatedEvent;
  'time-entry-deleted': PusherTimeEntryDeletedEvent;
};

export type ChannelRoadmapEventsMap = {
  'initiative-created': PusherInitiativeCreatedEvent;
  'initiative-updated': PusherInitiativeUpdatedEvent;
  'initiative-deleted': PusherInitiativeDeletedEvent;

  'milestone-created': PusherMilestoneCreatedEvent;
  'milestone-updated': PusherMilestoneUpdatedEvent;
  'milestone-deleted': PusherMilestoneDeletedEvent;

  'scenario-created': PusherScenarioCreatedEvent;
  'scenario-updated': PusherScenarioUpdatedEvent;
  'scenario-deleted': PusherScenarioDeletedEvent;
};

export type ChannelUserEvents = {
  'notification-created': PusherNotificationCreatedEvent;
  'job-updated': PusherJobUpdatedEvent;
  'ai-chat-message': PusherAIChatMessageEvent;
};

export type ChannelFolderBoardEvents = {
  'board-folder-created': PusherBoardFolderCreatedEvent;
  'board-folder-updated': PusherBoardFolderUpdatedEvent;

  'board-created': PusherBoardCreatedEvent;
  'board-updated': PusherBoardUpdatedEvent;
  'board-archived': PusherBoardArchivedEvent;
};

// Generic Pusher event types for events that don't have specific type definitions yet
type GenericPusherEvent = PusherEvent;

// Type for tracking chunked event assembly
interface ChunkedEventData {
  chunks: string[];
  receivedFinal: boolean;
}

export type ChannelEventsMap = ChannelItemEventsMap &
  ChannelCommentEventsMap &
  ChannelTimeTrackingEventsMap &
  ChannelRoadmapEventsMap &
  ChannelUserEvents &
  ChannelFolderBoardEvents & {
    // TODO: create specific types for these events when backend contract is finalized
    'active-timeentry-started': GenericPusherEvent;
    'active-timeentry-updated': GenericPusherEvent;
    'active-timeentry-stopped': GenericPusherEvent;
    'pull-request-updated': GenericPusherEvent;
    'permission-updated': PusherPermissionUpdatedEvent;
    'board-permission-updated': PusherBoardPermissionUpdatedEvent;
    'database-credential-updated': PusherDatabaseCredentialUpdatedEvent;
    'database-credential-deleted': PusherDatabaseCredentialDeletedEvent;
  };

export type LocalEventsMap = {
  'dropped-top-level': void;
  'org-members-updated': { members: Array<OrganizationMembership> };
  'organization-created': { organization: Organization };
  'team-created': { team: Team };
  'product-created': { product: Product };
  'notifications-updated': { notifications: Array<Notification> };
  'notification-read': { notification: Notification };
  'all-notifications-read': void;
  'item-deleted': PreparedItem;
  'item-archived': PreparedItem;
  'item-unarchived': { item: PreparedItem; unarchiveChildren: boolean };
  'item-created': PreparedItem;
  'automation-created': Workflow;
  'item-updated': PreparedItem;
  'automations-form-validation-failed': void;

  // Board folder entities
  'board-folder-created': BoardFolder;
  'board-folder-updated': BoardFolder;
  'board-folder-deleted': BoardFolder['id'];

  'board-created': Board;
  'board-updated': Board;
  'board-deleted': Board['id'];

  'cycle-created': Board;
  'cycle-updated': Board;
  'cycle-deleted': Board['id'];
};

export const ChannelEvents = mitt<ChannelEventsMap>();
export const LocalEvents = mitt<LocalEventsMap>();

let pusher: Pusher | null = null;

/**
 * Get the current Pusher socket ID for use in API requests.
 * This is used to exclude the sender from receiving their own broadcasts.
 */
export function getPusherSocketId(): string | undefined {
  return pusher?.connection.socket_id;
}

export function init(token: string): boolean {
  if (pusher) {
    return true;
  }
  if (import.meta.env.MODE === 'test') return true;
  try {
    if (import.meta.env.VITE_LOG_PUSHER_EVENTS === 'true') {
      Pusher.logToConsole = true;
    }
    pusher = new Pusher(import.meta.env.VITE_PUSHER_APP_KEY as string, {
      cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER as string,
      authorizer: (channel) => {
        return {
          authorize: async (socketId, callback) => {
            const response = await axiosInstance.get(
              '/v1/pusher/authenticate',
              {
                params: {
                  channelName: channel.name,
                  socketId,
                },
              }
            );
            callback(null, response.data);
          },
        };
      },
      auth: {
        headers: {
          Authorization: `${token}`,
        },
      },
    });

    const userStore = useUserStore();
    type PusherConnectionState =
      | 'initialized'
      | 'connecting'
      | 'connected'
      | 'unavailable'
      | 'failed'
      | 'disconnected';
    pusher.connection.bind(
      'state_change',
      (state: {
        previous: PusherConnectionState;
        current: PusherConnectionState;
      }) => {
        if (state.current === 'initialized' || state.current === 'connecting') {
          userStore.setPusherStatus({
            isLoading: true,
            connection: false,
            orgChannel: false,
            userChannel: false,
            teamChannels: false,
          });
        } else if (
          state.current === 'unavailable' ||
          state.current === 'failed' ||
          state.current === 'disconnected'
        ) {
          userStore.setPusherStatus({
            isLoading: false,
            connection: false,
            orgChannel: false,
            userChannel: false,
            teamChannels: false,
          });
        } else if (state.current === 'connected') {
          userStore.setPusherStatus({
            isLoading: false,
            connection: true,
          });
        }
      }
    );

    pusher.connection.bind('connected', () => {
      axiosInstance.defaults.headers['X-PUNO-SOCKET-ID'] =
        pusher?.connection.socket_id;
    });
    return true;
  } catch (e) {
    console.error('There was a problem initializing Pusher', e);
    return false;
  }
}

let organizationChannel: Channel | undefined;

export function initOrganizationChannel(activeOrganization: Organization) {
  if (!pusher) {
    console.warn(
      'Pusher was not initialized when trying to connect to the <<<org channel>>>. Live updates will not work.'
    );
    return;
  }
  const userStore = useUserStore();

  const channelName = `private-@organization@${activeOrganization.id}`;

  if (organizationChannel?.subscribed) {
    if (organizationChannel.name === channelName) return;
    organizationChannel.unsubscribe();
    userStore.setPusherStatus({ orgChannel: false });
  }
  organizationChannel = pusher.subscribe(channelName);
  const events: Record<string, ChunkedEventData> = {};
  organizationChannel.bind_global(
    async (
      eventName: keyof ChannelEventsMap,
      data: PusherEvent | ChunkedPusherEvent
    ) => {
      if (eventName.startsWith('CHUNKED-')) {
        data = data as ChunkedPusherEvent;
        if (!events[data.id]) {
          events[data.id] = { chunks: [], receivedFinal: false };
        }
        const ev = events[data.id];
        ev.chunks[data.index] = data.chunk;
        if (data.final) ev.receivedFinal = true;
        if (
          ev.receivedFinal &&
          ev.chunks.length === Object.keys(ev.chunks).length
        ) {
          ChannelEvents.emit(
            eventName.replace('CHUNKED-', '') as keyof ChannelEventsMap,
            JSON.parse(ev.chunks.join(''))
          );
          delete events[data.id];
        }
        return; // Don't emit raw chunked events
      }
      const regularData = data as PusherEvent;
      ChannelEvents.emit(eventName, regularData);
    }
  );

  organizationChannel.bind('pusher:subscription_succeeded', () => {
    userStore.setPusherStatus({ orgChannel: true });
  });
  organizationChannel.bind('pusher:subscription_error', () => {
    userStore.setPusherStatus({ orgChannel: false });
  });
}

let userChannel: Channel | undefined;

export function initUserChannel(user: User) {
  if (!pusher) {
    console.warn(
      'Pusher was not initialized when trying to connect to the <<<user channel>>>. Live updates will not work.'
    );
    return;
  }
  const userStore = useUserStore();

  const channelName = `private-@user@${user.id.toLowerCase()}`;

  if (userChannel?.subscribed) {
    if (userChannel.name === channelName) return;
    userChannel.unsubscribe();
    userStore.setPusherStatus({ userChannel: false });
  }
  userChannel = pusher.subscribe(channelName);
  const events: Record<string, ChunkedEventData> = {};
  userChannel.bind_global(
    async (
      eventName: keyof ChannelEventsMap,
      data: PusherEvent | ChunkedPusherEvent
    ) => {
      if (eventName.startsWith('CHUNKED-')) {
        data = data as ChunkedPusherEvent;
        if (!events[data.id]) {
          events[data.id] = { chunks: [], receivedFinal: false };
        }
        const ev = events[data.id];
        ev.chunks[data.index] = data.chunk;
        if (data.final) ev.receivedFinal = true;
        if (
          ev.receivedFinal &&
          ev.chunks.length === Object.keys(ev.chunks).length
        ) {
          const realEventName = eventName.replace(
            'CHUNKED-',
            ''
          ) as keyof ChannelEventsMap;
          const finalData = JSON.parse(ev.chunks.join(''));
          if (realEventName === 'job-updated') {
            const jobsStore = useJobsStore();
            jobsStore.updateJobCache(
              (finalData as PusherJobUpdatedEvent).data.jobAfter
            );
          }
          ChannelEvents.emit(realEventName, finalData);
          delete events[data.id];
        }
        return; // Don't emit raw chunked events
      }
      const regularData = data as PusherEvent;
      if (eventName === 'job-updated') {
        const jobsStore = useJobsStore();
        jobsStore.updateJobCache(
          (regularData as PusherJobUpdatedEvent).data.jobAfter
        );
      }
      ChannelEvents.emit(eventName, regularData);
    }
  );

  userChannel.bind('pusher:subscription_succeeded', () => {
    userStore.setPusherStatus({ userChannel: true });
  });
  userChannel.bind('pusher:subscription_error', () => {
    userStore.setPusherStatus({ userChannel: false });
  });
}

const teamChannels: Map<string, Channel> = new Map();
let teamChannelCount = 0;

export function initTeamChannels(organizationId: string, teamIds: string[]) {
  if (!pusher) {
    console.warn(
      'Pusher was not initialized when trying to connect to the <<<team channels>>>. Live updates will not work.'
    );
    return;
  }
  const userStore = useUserStore();

  // Clean up existing team channels
  cleanupTeamChannels();

  if (!teamIds || teamIds.length === 0) {
    userStore.setPusherStatus({ teamChannels: true });
    return;
  }

  teamChannelCount = teamIds.length;
  let subscribedCount = 0;
  let failedCount = 0;

  teamIds.forEach((teamId) => {
    const channelName = `private-@organization@${organizationId}@team@${teamId}`;
    const teamChannel = pusher!.subscribe(channelName);
    const events: Record<string, ChunkedEventData> = {};

    teamChannel.bind_global(
      async (
        eventName: keyof ChannelEventsMap,
        data: PusherEvent | ChunkedPusherEvent
      ) => {
        if (eventName.startsWith('CHUNKED-')) {
          data = data as ChunkedPusherEvent;
          if (!events[data.id]) {
            events[data.id] = { chunks: [], receivedFinal: false };
          }
          const ev = events[data.id];
          ev.chunks[data.index] = data.chunk;
          if (data.final) ev.receivedFinal = true;
          if (
            ev.receivedFinal &&
            ev.chunks.length === Object.keys(ev.chunks).length
          ) {
            ChannelEvents.emit(
              eventName.replace('CHUNKED-', '') as keyof ChannelEventsMap,
              JSON.parse(ev.chunks.join(''))
            );
            delete events[data.id];
          }
          return; // Don't emit raw chunked events
        }
        const regularData = data as PusherEvent;
        ChannelEvents.emit(eventName, regularData);
      }
    );

    teamChannel.bind('pusher:subscription_succeeded', () => {
      subscribedCount++;
      if (subscribedCount + failedCount === teamChannelCount) {
        userStore.setPusherStatus({ teamChannels: true });
      }
    });

    teamChannel.bind('pusher:subscription_error', () => {
      failedCount++;
      if (subscribedCount + failedCount === teamChannelCount) {
        userStore.setPusherStatus({ teamChannels: failedCount === 0 });
      }
    });

    teamChannels.set(teamId, teamChannel);
  });
}

export function cleanupTeamChannels() {
  const userStore = useUserStore();
  teamChannels.forEach((channel) => {
    if (channel.subscribed) {
      channel.unsubscribe();
    }
  });
  teamChannels.clear();
  teamChannelCount = 0;
  userStore.setPusherStatus({ teamChannels: false });
}

/**
 * Subscribe to a Pusher presence channel for real-time member tracking.
 * Returns the channel object for binding to presence events.
 *
 * @param channelName - The presence channel name (must start with 'presence-')
 * @returns The channel object, or null if Pusher is not initialized
 */
export function subscribeToPresenceChannel(
  channelName: string
): Channel | null {
  if (!pusher) {
    console.warn(
      'Pusher was not initialized when trying to subscribe to presence channel. Live updates will not work.'
    );
    return null;
  }
  return pusher.subscribe(channelName);
}

/**
 * Unsubscribe from a Pusher channel.
 *
 * @param channel - The channel to unsubscribe from
 */
export function unsubscribeFromChannel(channel: Channel): void {
  if (channel.subscribed) {
    channel.unsubscribe();
  }
}
